import { useContext } from "react";
import { IconDefinition } from "@fortawesome/free-solid-svg-icons";
import { Context } from "../App";
import { Icons } from "../base-components/Icons";
import { RoutePath } from "./config";
import { useScopes } from "@/model/Token";

export type NavItem = { path: string; title: string; icon: IconDefinition };
export type NavSection = { title: string | null; items: NavItem[] };

/**
 * The app's menu, filtered by the signed-in user's role. One source for the
 * sidebar and the Ctrl+K command palette, so they can never disagree.
 */
export const useNavSections = (): NavSection[] => {
    const { translations: t } = useContext(Context);
    const scope = useScopes();
    const isAdmin = scope.includes("ADMIN") || scope.includes("SUPERUSER");
    const isSuperUser = scope.includes("SUPERUSER");

    if (!isAdmin) {
        return [{
            title: null,
            items: [
                { path: RoutePath.Tasks, title: t.tasks.title, icon: Icons.Task },
                { path: RoutePath.Orders, title: t.orders.title, icon: Icons.Order },
                { path: RoutePath.Worksites, title: t.worksites.title, icon: Icons.Worksite },
                { path: RoutePath.Customers, title: t.customers.title, icon: Icons.Customer },
            ],
        }];
    }

    const sections: NavSection[] = [
        {
            title: t.sidebar.administration,
            items: [
                { path: RoutePath.Dashboard, title: t.dashboard.title, icon: Icons.Dashboard },
                { path: RoutePath.Overview, title: t.graph.title, icon: Icons.Chart },
                { path: RoutePath.Worksites, title: t.worksites.title, icon: Icons.Worksite },
                { path: RoutePath.Customers, title: t.customers.title, icon: Icons.Customer },
                { path: RoutePath.Orders, title: t.orders.title, icon: Icons.Order },
                { path: RoutePath.Tasks, title: t.tasks.title, icon: Icons.Task },
            ],
        },
        {
            title: t.sidebar.configuration,
            items: [
                { path: RoutePath.Products, title: t.products.title, icon: Icons.Product },
                { path: RoutePath.Resources, title: t.resources.title, icon: Icons.Resource },
                { path: RoutePath.Properties, title: t.properties.title, icon: Icons.Property },
                { path: RoutePath.SystemSettings, title: t.systemSettings.title, icon: Icons.Settings },
            ],
        },
        {
            title: t.sidebar.importExport,
            items: [
                { path: RoutePath.CustomerImport, title: t.importExport.customerImport, icon: Icons.Import },
                { path: RoutePath.WorksiteImport, title: t.importExport.worksiteImport, icon: Icons.Import },
            ],
        },
        {
            title: t.sidebar.developer,
            items: [
                { path: RoutePath.TemplateEditor, title: t.templateEditor.title, icon: Icons.Template },
            ],
        },
    ];
    if (isSuperUser) {
        sections.push({
            title: t.sidebar.superAdmin,
            items: [{ path: RoutePath.Users, title: t.userManagement.title, icon: Icons.UserManagement }],
        });
    }
    return sections;
};
